/**
 * The side navigation of an administration area (Company today; Workspace
 * Controls is the next candidate): one route-backed entry per section.
 *
 *   - On `md` and up: a column beside the content, with an optional heading
 *     per group.
 *   - Below `md`: a compact bar naming the current section, whose menu
 *     button opens a left-hand drawer listing every section (the email
 *     arche's mobile pattern). A scrolling row of tabs stops working at a
 *     dozen entries; a drawer does not.
 *
 * Route-backed on purpose — each section is directly addressable, so a link
 * pasted into a thread or a refresh lands on the same section. `NavLink`
 * marks the active entry with `aria-current="page"`.
 *
 * Groups: an item may carry a `group` label. Items are listed in the order
 * given; a heading is drawn where the group changes, and only when at least
 * one item names a group — five ungrouped entries render as a flat list.
 */

import { useState } from 'react';
import { Menu, type LucideIcon } from 'lucide-react';
import { NavLink, matchPath, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

export interface SettingsNavItem {
  to: string;
  label: string;
  icon?: LucideIcon;
  /** Match the path exactly (default) or as a prefix. */
  end?: boolean;
  /** Optional heading the item sits under; consecutive items share one. */
  group?: string;
}

type Run = { group: string | undefined; items: SettingsNavItem[] };

/** Consecutive items with the same group, in the order given. */
function runs(items: readonly SettingsNavItem[]): Run[] {
  const out: Run[] = [];
  for (const item of items) {
    const last = out[out.length - 1];
    if (last && last.group === item.group) last.items.push(item);
    else out.push({ group: item.group, items: [item] });
  }
  return out;
}

function NavList({
  items,
  onNavigate,
  idPrefix,
}: {
  items: readonly SettingsNavItem[];
  onNavigate?: () => void;
  idPrefix: string;
}) {
  const grouped = items.some((i) => i.group !== undefined);
  return (
    <div className="flex flex-col gap-4">
      {runs(items).map((run, i) => {
        const headingId = run.group ? `${idPrefix}-group-${i}` : undefined;
        return (
          <div key={i} className="flex flex-col gap-1">
            {grouped && run.group && (
              <p
                id={headingId}
                className="px-3 pb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground"
              >
                {run.group}
              </p>
            )}
            <ul className="flex flex-col gap-1" aria-labelledby={headingId}>
              {run.items.map(({ to, label, icon: Icon, end = true }) => (
                <li key={to}>
                  <NavLink
                    to={to}
                    end={end}
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        isActive
                          ? 'bg-muted font-medium text-foreground'
                          : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                      )
                    }
                  >
                    {Icon && <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />}
                    {label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

export function SettingsSideNav({
  items,
  label,
  className,
}: {
  items: readonly SettingsNavItem[];
  /** Names the landmark for assistive tech, e.g. "Company sections". */
  label: string;
  className?: string;
}) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const current =
    items.find((i) => matchPath({ path: i.to, end: i.end ?? true }, pathname)) ?? null;
  const CurrentIcon = current?.icon;

  return (
    <>
      {/* md and up: the column. */}
      <nav aria-label={label} className={cn('hidden min-w-0 md:block', className)}>
        <NavList items={items} idPrefix="settings-nav" />
      </nav>

      {/* Below md: the bar naming the current section, and the drawer. */}
      <div className={cn('md:hidden', className)}>
        <div className="flex items-center gap-2 rounded-md border border-border bg-card px-2 py-1.5">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            aria-label={`Open ${label.toLowerCase()}`}
            onClick={() => setOpen(true)}
          >
            <Menu className="h-4 w-4" aria-hidden="true" />
          </Button>
          <span className="flex min-w-0 items-center gap-2 text-sm font-medium text-foreground">
            {CurrentIcon && <CurrentIcon className="h-4 w-4 shrink-0" aria-hidden="true" />}
            <span className="truncate">{current?.label ?? label}</span>
          </span>
        </div>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetContent side="left" className="flex w-72 flex-col gap-0">
            <SheetHeader className="mb-4 text-left">
              <SheetTitle>{label}</SheetTitle>
              <SheetDescription className="sr-only">Choose a section to open.</SheetDescription>
            </SheetHeader>
            <SheetBody>
              <nav aria-label={label}>
                <NavList
                  items={items}
                  idPrefix="settings-drawer"
                  onNavigate={() => setOpen(false)}
                />
              </nav>
            </SheetBody>
          </SheetContent>
        </Sheet>
      </div>
    </>
  );
}
